import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { ArrowLeft, Bell, BellSlash, Trophy, Fire, Star, Briefcase, Check, Trash } from '@phosphor-icons/react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const ICON_MAP = {
  trophy: Trophy,
  fire: Fire,
  star: Star,
  briefcase: Briefcase,
  bell: Bell,
};

const TYPE_TONE = {
  podium_rank:  'border-[#facc15]/40 from-[#facc15]/12 text-[#facc15]',
  podium_nudge: 'border-orange-500/40 from-orange-500/12 text-orange-300',
  referral_signup: 'border-[#c084fc]/40 from-[#c084fc]/12 text-[#c084fc]',
  referral_invested: 'border-emerald-500/40 from-emerald-500/12 text-emerald-300',
  fraternity_invite: 'border-[#22d3ee]/40 from-[#22d3ee]/12 text-[#22d3ee]',
  system: 'border-white/15 from-white/8 text-white/70',
};

const Notifications = () => {
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [unread, setUnread] = useState(0);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');

  const load = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const res = await axios.get(`${BACKEND_URL}/api/notifications/me?limit=100`,
        { headers: { Authorization: `Bearer ${token}` } });
      setItems(res.data.notifications || []);
      setUnread(res.data.unread_count || 0);
    } catch (_) {} finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const markAllRead = async () => {
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${BACKEND_URL}/api/notifications/mark-read`, {},
        { headers: { Authorization: `Bearer ${token}` } });
      toast.success('All read');
      load();
    } catch (_) {}
  };

  const dismiss = async (id) => {
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${BACKEND_URL}/api/notifications/${id}`,
        { headers: { Authorization: `Bearer ${token}` } });
      setItems(items.filter((n) => n.id !== id));
    } catch (_) {}
  };

  const openCta = async (n) => {
    if (!n.read) {
      try {
        const token = localStorage.getItem('token');
        await axios.post(`${BACKEND_URL}/api/notifications/mark-read`,
          { notification_ids: [n.id] },
          { headers: { Authorization: `Bearer ${token}` } });
      } catch (_) {}
    }
    if (n.cta_url) navigate(n.cta_url);
  };

  const filtered = filter === 'all'
    ? items
    : filter === 'unread'
    ? items.filter((n) => !n.read)
    : items.filter((n) => n.type.startsWith(filter));

  return (
    <div className="min-h-screen text-white" style={{ background: '#080414', fontFamily: "'Poppins', system-ui, sans-serif" }} data-testid="notifications-page">
      <header data-web-chrome="nav" className="border-b border-white/10 bg-black/40 backdrop-blur-md sticky top-0 z-30">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 py-4 flex items-center justify-between">
          <button onClick={() => navigate(-1)} className="text-sm text-white/70 hover:text-white flex items-center gap-1.5" data-testid="notif-back">
            <ArrowLeft size={14} /> Back
          </button>
          <h1 className="text-base font-bold flex items-center gap-2">
            Notifications
            {unread > 0 && (
              <span className="text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-full bg-[#facc15] text-black font-extrabold" data-testid="notif-unread-pill">
                {unread} new
              </span>
            )}
          </h1>
          <button
            onClick={markAllRead}
            disabled={unread === 0}
            data-testid="notif-mark-all"
            className="text-xs text-white/70 hover:text-white disabled:opacity-30 disabled:cursor-not-allowed flex items-center gap-1"
          >
            <Check size={12} /> Mark all read
          </button>
        </div>
      </header>

      <div className="max-w-3xl mx-auto px-4 sm:px-6 py-8">
        {/* Filter chips */}
        <div className="flex flex-wrap gap-2 mb-5" data-testid="notif-filters">
          {[
            { id: 'all', label: 'All' },
            { id: 'unread', label: 'Unread' },
            { id: 'podium', label: '🏆 Podiums' },
            { id: 'referral', label: '🤝 Referrals' },
            { id: 'fraternity', label: '🎬 Fraternity' },
          ].map((f) => (
            <button
              key={f.id}
              data-testid={`notif-filter-${f.id}`}
              onClick={() => setFilter(f.id)}
              className={`text-[11px] uppercase tracking-wider px-3 py-1.5 rounded-full border font-bold transition-colors ${
                filter === f.id ? 'bg-white text-black border-white' : 'bg-white/5 border-white/15 text-white/70 hover:border-white/30'
              }`}
            >
              {f.label}
            </button>
          ))}
        </div>

        {loading ? (
          <div className="text-white/70 text-sm">Loading…</div>
        ) : filtered.length === 0 ? (
          <div className="bg-white/5 border border-dashed border-white/15 rounded-xl p-10 text-center" data-testid="notif-empty">
            <BellSlash size={36} className="mx-auto text-white/70 mb-3" />
            <h3 className="text-base font-bold mb-1">All caught up</h3>
            <p className="text-xs text-white/70 font-light">Nothing to report — keep climbing the podium.</p>
          </div>
        ) : (
          <div className="space-y-2.5" data-testid="notif-list">
            {filtered.map((n) => {
              const Icon = ICON_MAP[n.icon] || Bell;
              const tone = TYPE_TONE[n.type] || TYPE_TONE.system;
              return (
                <div
                  key={n.id}
                  data-testid={`notif-row-${n.id}`}
                  className={`relative bg-gradient-to-r ${tone} to-transparent border rounded-xl p-4 flex items-start gap-3 ${!n.read ? 'shadow-[0_0_0_1px_rgba(250,204,21,0.15)]' : ''}`}
                >
                  {!n.read && <span className="absolute top-3 right-3 w-2 h-2 rounded-full bg-[#facc15]" />}
                  <Icon size={22} weight="fill" className="flex-shrink-0 mt-0.5" />
                  <div className="flex-1 min-w-0">
                    <h3 className="text-sm font-extrabold leading-tight mb-1">{n.title}</h3>
                    <p className="text-xs text-white/70 leading-relaxed font-light mb-2">{n.body}</p>
                    <div className="flex items-center gap-2 text-[10px] text-white/70">
                      <span>{new Date(n.created_at).toLocaleString()}</span>
                      {n.cta_url && (
                        <button
                          data-testid={`notif-cta-${n.id}`}
                          onClick={() => openCta(n)}
                          className="ml-auto bg-white text-black text-[10px] font-extrabold px-3 py-1 rounded-full hover:scale-105 transition-transform"
                        >
                          {n.cta_label || 'Open'} →
                        </button>
                      )}
                      <button
                        data-testid={`notif-dismiss-${n.id}`}
                        onClick={() => dismiss(n.id)}
                        className="text-white/70 hover:text-rose-400"
                        aria-label="Dismiss"
                      >
                        <Trash size={12} />
                      </button>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

export default Notifications;
